import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import Topbar from '../../components/topbar';
import DynamicAdminTable, { Column } from '../../components/admin-table';
import EditSubscriptionAddonModal, { NewCapacityAddon } from '../../components/popup/edit-subscription-addon';
import DeletePopup from '../../components/popup/delete';
import { useToast } from '../../providers/toaster-provider';
import { AddonConfig, addonLabel, addonDescription, parseCapacityKey, useSubscriptionTiers } from '../../util/subscription-tiers';
import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';

// The add-on catalog (SubscriptionAddons) - priced once for every plan. A
// tier only says what it includes (Admin > Subscription Tiers); anything a
// paid tier doesn't include can be bought as the add-on at these prices.
// Capacity add-ons (more users, a longer projection horizon) are added here,
// as many as needed - e.g. "5 more users" and "25 more users".
const SubscriptionAddonsAdmin: React.FC = () => {
    const { showSuccess, showError } = useToast();
    const { refetch: refetchTiers } = useSubscriptionTiers();
    const [addons, setAddons] = useState<AddonConfig[]>([]);
    const [editing, setEditing] = useState<AddonConfig | null>(null);
    const [creating, setCreating] = useState(false);
    const [deleting, setDeleting] = useState<AddonConfig | null>(null);
    const [listApi, setListApi] = useApi<any>();
    const [updateApi, setUpdateApi] = useApi<any>();
    const [createApi, setCreateApi] = useApi<any>();
    const [deleteApi, setDeleteApi] = useApi<any>();

    useEffect(() => { document.title = 'FineAnts Admin | Subscription Add-ons'; }, []);

    const fetchAddons = useCallback(() => {
        setListApi(buildApiRequestConfig(routes.subscriptionTiers.addons));
    }, [setListApi]);

    useEffect(() => { fetchAddons(); }, [fetchAddons]);

    useEffect(() => {
        if (listApi.data?.success) setAddons(listApi.data.addons || []);
        if (listApi.error) showError(typeof listApi.error === 'string' ? listApi.error : 'Failed to load add-ons');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listApi.data, listApi.error]);

    useEffect(() => {
        const { data, isLoading, error } = updateApi;
        if (data && !isLoading) {
            showSuccess('Saved');
            fetchAddons();
            // Each tier carries its add-on prices - checkout picks the change up.
            refetchTiers();
        }
        if (error) showError(typeof error === 'string' ? error : 'Failed to save add-on');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateApi]);

    useEffect(() => {
        const { data, isLoading, error } = createApi;
        if (data && !isLoading) {
            showSuccess('Add-on added');
            setCreating(false);
            if (data.addons) setAddons(data.addons);
            refetchTiers();
        }
        if (error) showError(typeof error === 'string' ? error : 'Failed to add add-on');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createApi]);

    useEffect(() => {
        const { data, isLoading, error } = deleteApi;
        if (data && !isLoading) {
            showSuccess('Deleted');
            setDeleting(null);
            if (data.addons) setAddons(data.addons);
            refetchTiers();
        }
        if (error) {
            // e.g. companies still have it - the message says so.
            showError(typeof error === 'string' ? error : 'Failed to delete');
            setDeleting(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deleteApi]);

    const handleCreate = (addon: NewCapacityAddon) => {
        const { url, method } = buildApiRequestConfig(routes.subscriptionTiers.createAddon);
        setCreateApi({ url, method, body: addon });
    };

    const handleDelete = () => {
        if (!deleting) return;
        const { url, method } = buildApiRequestConfig(routes.subscriptionTiers.deleteAddon, [{ param: 'key', value: deleting.addonKey }]);
        setDeleteApi({ url, method });
    };

    const save = (addonKey: string, body: Partial<AddonConfig>) => {
        const { url, method } = buildApiRequestConfig(routes.subscriptionTiers.updateAddon, [{ param: 'key', value: addonKey }]);
        setUpdateApi({ url, method, body });
    };

    const money = (n: number) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

    // Plan features (Documents, Xero, ...) are priced in each tier's editor - only
    // the add-ons created here are listed.
    const rows = addons.filter(a => parseCapacityKey(a.addonKey)).map(a => {
        return {
            ...a,
            id: a.addonKey,
            label: addonLabel(a.addonKey),
            description: addonDescription(a.addonKey),
            pricePerMonthDisplay: money(a.pricePerMonth),
            pricePerYearDisplay: money(a.pricePerYear),
        };
    });

    const columns: Column[] = [
        { id: 'label', label: 'Add-on', size: 200, disabled: true },
        { id: 'description', label: 'Description', size: 320, disabled: true },
        { id: 'pricePerMonthDisplay', label: 'No commitment (per month)', size: 170, disabled: true },
        { id: 'pricePerYearDisplay', label: '12 months up front (per year)', size: 190, disabled: true },
        {
            // Off = not sold as an add-on; plans that include it keep it.
            id: 'isOffered', label: 'Offered', type: 'toggle', size: 90,
            onToggle: (row, val) => save(row.addonKey, { isOffered: val }),
        },
        {
            id: 'actions', label: 'Actions', type: 'actions', size: 80,
            onEdit: (row) => setEditing(addons.find(a => a.addonKey === row.addonKey) ?? null),
            // Only capacity add-ons can be deleted - features are fixed.
            onDelete: (row) => {
                if (!parseCapacityKey(row.addonKey)) {
                    showError("Plan features can't be deleted - turn off Offered instead");
                    return;
                }
                setDeleting(addons.find(a => a.addonKey === row.addonKey) ?? null);
            },
        },
    ];

    return (
        <div style={{ width: '100%' }}>
            <EditSubscriptionAddonModal
                open={creating}
                addon={null}
                saving={createApi.isLoading}
                onClose={() => setCreating(false)}
                onCreate={handleCreate}
                onSave={() => {}}
            />
            <DeletePopup
                open={!!deleting}
                red
                message={`Delete ${deleting ? addonLabel(deleting.addonKey) : ''}?`}
                description="Only an add-on no company has can be deleted. To stop selling it but keep it for companies that have it, turn off Offered instead."
                confirmLabel="Delete"
                loading={deleteApi.isLoading}
                onConfirm={handleDelete}
                onCancel={() => setDeleting(null)}
            />
            <EditSubscriptionAddonModal
                open={!!editing}
                addon={editing}
                onClose={() => setEditing(null)}
                onSave={(updated) => {
                    save(updated.addonKey, { isOffered: updated.isOffered, pricePerMonth: updated.pricePerMonth, pricePerYear: updated.pricePerYear });
                    setEditing(null);
                }}
            />
            <Topbar title="Subscription Add-ons" />
            <div style={{ padding: '10px', background: 'var(--color-page-background)', height: 'calc(100vh - 86px)' }}>
                <Box mb={1} display="flex" alignItems="flex-start" gap={2}>
                    <Typography fontSize={13} color="var(--color-text-muted)" flex={1}>
                        Sold on every paid plan that doesn't already include it (set in Subscription Tiers). A company turns an add-on on itself in Company Settings &gt; Subscription. A new price applies to new add-on charges - Stripe keeps existing subscribers on theirs.
                    </Typography>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => setCreating(true)}
                        sx={{ textTransform: 'none', background: 'var(--color-primary)', color: '#fff', boxShadow: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                    >
                        Add add-on
                    </Button>
                </Box>
                <DynamicAdminTable columns={columns} data={rows} />
            </div>
        </div>
    );
};

export default SubscriptionAddonsAdmin;
